import React from 'react';
import { CircularProgress } from '@mui/material';
import { SimpleFormatNumber, formatNumber } from 'src/utils/utility';
import IconDollar from 'src/assets/images/icons/dollar-circle.svg';
import IconDollarYellow from 'src/assets/images/icons/dollar-circle-yellow.svg';
import format from 'date-fns/format';

interface CreditMetricItemProps {
  label: string;
  value: number | undefined;
  isLoading: boolean;
  expiry?: string;
}

interface CreditMetricContainerProps {
  title: string;
  items: CreditMetricItemProps[];
}

const CreditMetricItem: React.FC<CreditMetricItemProps> = ({ label, value, isLoading, expiry }) => {
  const isDateFormat = expiry && /^[A-Za-z]{3} \d{1,2}, \d{4}$/.test(expiry);

  let expirationDate;
  if (isDateFormat || !expiry) {
    expirationDate = expiry ? `Expire on ${format(new Date(expiry), 'dd MMM yyyy')}` : 'Expire on -';
  } else {
    expirationDate = expiry ? `In ${expiry}` : 'In -';
  }

  const creditAmount = SimpleFormatNumber(value);
  return isLoading ? (
    <div className='flex-1 flex items-center justify-center my-6'>
      <CircularProgress size={24} />
    </div>
  ) : (
    <div className='flex-1 py-12 px-18'>
      <div className='mt-1 text-neutral-60 text-12 xl:text-14 font-semibold'>{label}</div>
      <div className='py-6 pl-6 pr-32 rounded-full flex items-center gap-12 min-w-[10rem]'>
        <div className={`w-28 h-28 rounded-full ${label.includes('Shipment') ? 'bg-[#F798241F]' : 'bg-[#E3F5F1]'} flex items-center justify-center`}>
          <img src={label.includes('Shipment') ? IconDollarYellow : IconDollar} alt='Dollar Icon' />
        </div>
        {isLoading ? (
          <CircularProgress color='primary' />
        ) : (
          <div className='flex flex-col'>
            <p className='flex items-center gap-6'>
              <strong className='text-16 font-bold text-neutral-80'>{creditAmount}</strong>
            </p>
            <p className={`text-10  ${isDateFormat ? 'text-red-40' : 'text-neutral-60'} `}>{expirationDate}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export const CreditMetricContainer: React.FC<CreditMetricContainerProps> = ({ title, items }) => {
  return (
    <div className={'rounded-lg shadow-lg border border-neutral-20 m-4'}>
      {/* Title Row */}
      <div className='mx-20 my-8'>
        <p className='text-15 font-semibold text-gray-900'>{title}</p>
      </div>

      {/* Content Row with dividers between items */}
      <div className='flex bg-white border-t border-neutral-20 rounded-b-lg rounded-t-lg'>
        {items.map((item, index) => (
          <React.Fragment key={item.label}>
            <CreditMetricItem label={item.label} value={item.value} isLoading={item.isLoading} expiry={item.expiry} />
            {index < items.length - 1 && <div className='w-px bg-neutral-20'></div>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
